import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import Card, { CardHeader, CardTitle } from '../ui/Card.tsx';

interface CardMover {
  cardId: string;
  name: string;
  setName: string;
  imageUrl?: string;
  currentPrice: number;
  previousPrice: number;
  change: number;
  changePercent: number;
}

interface TopMoversWidgetProps {
  gainers: CardMover[];
  losers: CardMover[];
  loading?: boolean;
  onCardClick?: (cardId: string) => void;
}

export default function TopMoversWidget({
  gainers,
  losers,
  loading = false,
  onCardClick
}: TopMoversWidgetProps) {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const formatPercent = (percent: number) => {
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <Card variant="elevated" padding="lg">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-700 rounded w-32 mb-4" />
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex gap-3">
                <div className="w-10 h-14 bg-gray-700 rounded" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-700 rounded w-24 mb-2" />
                  <div className="h-3 bg-gray-700 rounded w-16" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  const MoverCard = ({
    card,
    isGainer
  }: {
    card: CardMover;
    isGainer: boolean;
  }) => (
    <button
      onClick={() => onCardClick?.(card.cardId)}
      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-800/50 transition-colors w-full text-left group"
    >
      <div className="w-10 h-14 bg-gray-800 rounded overflow-hidden flex-shrink-0">
        {card.imageUrl ? (
          <img
            src={card.imageUrl}
            alt={card.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">
            ?
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate group-hover:text-blue-400 transition-colors">
          {card.name}
        </p>
        <p className="text-xs text-gray-500 truncate">{card.setName}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-medium text-white">
          {formatPrice(card.currentPrice)}
        </p>
        <p
          className={`text-xs font-medium ${
            isGainer ? 'text-green-400' : 'text-red-400'
          }`}
        >
          {formatPercent(card.changePercent)}
        </p>
      </div>
    </button>
  );

  return (
    <Card variant="elevated" padding="lg">
      <CardHeader>
        <CardTitle>Top Movers</CardTitle>
      </CardHeader>

      <div className="grid grid-cols-2 gap-4">
        {/* Gainers */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 bg-green-500/20 rounded-lg">
              <TrendingUp className="w-4 h-4 text-green-400" />
            </div>
            <span className="text-sm font-medium text-green-400">Gainers</span>
          </div>
          <div className="space-y-1">
            {gainers.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">
                No gainers today
              </p>
            ) : (
              gainers.slice(0, 3).map(card => (
                <MoverCard key={card.cardId} card={card} isGainer={true} />
              ))
            )}
          </div>
        </div>

        {/* Losers */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 bg-red-500/20 rounded-lg">
              <TrendingDown className="w-4 h-4 text-red-400" />
            </div>
            <span className="text-sm font-medium text-red-400">Losers</span>
          </div>
          <div className="space-y-1">
            {losers.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">
                No losers today
              </p>
            ) : (
              losers.slice(0, 3).map(card => (
                <MoverCard key={card.cardId} card={card} isGainer={false} />
              ))
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
